import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { FaCamera, FaTimes } from 'react-icons/fa';
import useLanguage from '../hooks/useLanguage';
import cleaningChecklistApi from '../services/cleaningChecklistApi';
import {
  TASK_PHOTO_MAX_BYTES as MAX_FILE_SIZE,
  TASK_PHOTO_MIME_TYPES as ALLOWED_TYPES
} from '../config/taskPhotoRules';

/**
 * Report a problem found during a clean → spawns a linked maintenance task.
 *
 * A cleaning task records what was found; anything still needing work becomes
 * its own task. Kept to category + description + one optional photo — no issues
 * module, per plan.
 *
 * Photo validation shares the crew card's limits (config/taskPhotoRules.js).
 */

// Values match the backend ISSUE_CATEGORIES keys in taskRoutes.js
const CATEGORIES = ['electrical', 'plumbing', 'furniture', 'appliances', 'other'];

const ReportIssueModal = ({ show, onHide, taskId, onReported }) => {
  const { t, isArabic } = useLanguage();

  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (show) {
      setCategory('');
      setDescription('');
      setPhoto(null);
      setPhotoPreview(null);
      setError(null);
    }
  }, [show]);

  // Free the preview blob when it's replaced or the modal unmounts
  useEffect(() => {
    return () => { if (photoPreview) URL.revokeObjectURL(photoPreview); };
  }, [photoPreview]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError(null);
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError(t('tasks.photos.invalidType'));
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError(t('tasks.photos.tooLarge'));
      return;
    }
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    if (!category) { setError(t('tasks.reportIssue.categoryRequired')); return; }
    if (!description.trim()) { setError(t('tasks.reportIssue.descriptionRequired')); return; }

    setSubmitting(true);
    setError(null);
    try {
      const result = await cleaningChecklistApi.reportIssue(taskId, {
        category,
        description: description.trim(),
        photo: photo || undefined
      });
      if (onReported) onReported(result.task);
      onHide();
    } catch (err) {
      console.error('Report issue failed:', err);
      setError(err?.response?.data?.error || t('tasks.reportIssue.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={submitting ? undefined : onHide}
      centered
      fullscreen="sm-down"
      dir={isArabic ? 'rtl' : 'ltr'}
      backdrop={submitting ? 'static' : true}
      keyboard={!submitting}
    >
      <Modal.Header closeButton={!submitting} dir={isArabic ? 'rtl' : 'ltr'}>
        <Modal.Title style={{ fontSize: '1.05rem', color: '#471396' }}>
          🛠️ {t('tasks.reportIssue.title')}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body dir={isArabic ? 'rtl' : 'ltr'} style={{ textAlign: isArabic ? 'right' : 'left' }}>
        {error && (
          <div
            className="mb-2 p-2 rounded small fw-semibold"
            role="alert"
            style={{ background: '#fdecea', borderInlineStart: '3px solid #DC3545', color: '#090040' }}
          >
            {error}
          </div>
        )}

        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold">{t('tasks.reportIssue.category')}</Form.Label>
          <Form.Select
            className="touch-select"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            dir={isArabic ? 'rtl' : 'ltr'}
          >
            <option value="">{t('tasks.reportIssue.categoryPlaceholder')}</option>
            {CATEGORIES.map(c => (
              <option key={c} value={c}>{t(`tasks.reportIssue.${c}`)}</option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold">{t('tasks.reportIssue.description')}</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            className="touch-input"
            value={description}
            maxLength={1000}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('tasks.reportIssue.descriptionPlaceholder')}
            dir={isArabic ? 'rtl' : 'ltr'}
            style={{ textAlign: isArabic ? 'right' : 'left' }}
          />
        </Form.Group>

        {/* Optional photo */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
        {photoPreview ? (
          <div className="d-flex align-items-center gap-2 mb-2">
            <div style={{ position: 'relative', width: '80px', height: '60px', borderRadius: '6px', overflow: 'hidden', border: '2px solid #FF5722' }}>
              <img src={photoPreview} alt="issue" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            <Button variant="link" size="sm" className="text-danger p-0" onClick={() => { setPhoto(null); setPhotoPreview(null); }}>
              <FaTimes /> {t('tasks.reportIssue.changePhoto')}
            </Button>
          </div>
        ) : (
          <Button
            variant="outline-secondary"
            className="w-100 touch-btn mb-2"
            onClick={() => fileInputRef.current?.click()}
          >
            <FaCamera /> {t('tasks.reportIssue.addPhoto')}
          </Button>
        )}
      </Modal.Body>

      <Modal.Footer className="touch-modal-actions" dir={isArabic ? 'rtl' : 'ltr'}>
        <Button variant="light" className="touch-btn" onClick={onHide} disabled={submitting}>
          {t('tasks.reportIssue.cancel')}
        </Button>
        <Button
          variant="primary"
          className="fw-bold touch-btn"
          style={{ background: '#471396', borderColor: '#471396' }}
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? <Spinner animation="border" size="sm" /> : t('tasks.reportIssue.submit')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ReportIssueModal;
